// A CPU driver: given a car and a goal point, produces CarInput each step. Replans when it strays,
// reverses when stuck, and eases off near the goal. Also the brain behind the headless AI player.

import { DEFAULT_TUNING, type CarInput, type CarState } from './car';
import { Navigator, pursue, type Waypoint } from './nav';

export interface BotOptions { skill: number; /** 0..1: affects speed and steering sloppiness */ topSpeed?: number; /** fraction of the car's top speed to cruise at (default 0.72) */ }

export class BotDriver {
  path: Waypoint[] | null = null;
  private idx = 0;
  private goal: { x: number; z: number } | null = null;
  private replanAt = 0;
  private stuckTime = 0;
  private reverseUntil = 0;
  private time = 0;
  readonly input: CarInput = { steer: 0, brake: 0, nitro: false, throttle: 1 };
  wanderSeed = Math.random() * 1000;

  constructor(readonly nav: Navigator, readonly opts: BotOptions = { skill: 0.7 }) {}

  setGoal(x: number, z: number): void {
    if (this.goal && Math.hypot(this.goal.x - x, this.goal.z - z) < 2) return;
    this.goal = { x, z }; this.path = null; this.replanAt = 0;
  }
  clearGoal(): void { this.goal = null; this.path = null; }
  hasGoal(): boolean { return !!this.goal; }
  distanceToGoal(car: CarState): number { return this.goal ? Math.hypot(this.goal.x - car.x, this.goal.z - car.z) : Infinity; }

  private stuckSpots: { x: number; z: number; r: number; cost: number; until: number }[] = [];
  private lastSteer = 0;

  step(car: CarState, dt: number): CarInput {
    this.time += dt;
    const inp = this.input;
    inp.nitro = false; inp.throttle = 1;
    if (!this.goal) { inp.steer = 0; inp.brake = 1; return inp; }

    // stuck detection: wanting to move but not moving
    if (Math.abs(car.speed) < 0.8 && this.time > this.reverseUntil && this.distanceToGoal(car) > 6) this.stuckTime += dt; else if (Math.abs(car.speed) > 2) this.stuckTime = 0;
    if (this.stuckTime > 1.0) {
      this.stuckTime = 0; this.reverseUntil = this.time + 1.3 + Math.random() * 0.7; this.path = null;
      // remember where we got stuck so the next plan routes around it
      this.stuckSpots.push({ x: car.x, z: car.z, r: 7, cost: 25, until: this.time + 40 });
    }
    if (this.time < this.reverseUntil) { inp.brake = 1; inp.steer = -Math.sign(this.lastSteer || 1); return inp; }

    let needPlan = !this.path || this.time >= this.replanAt || this.idx >= this.path.length;
    if (!needPlan && this.path) {
      const p = pursue(car, this.path, this.idx);
      if (p.offPath > 6) needPlan = true;
    }
    if (needPlan) {
      this.stuckSpots = this.stuckSpots.filter((s) => s.until > this.time);
      this.nav.setAvoid(this.stuckSpots);
      this.path = this.nav.findPath(car.x, car.z, this.goal.x, this.goal.z);
      this.nav.setAvoid([]);
      this.idx = 0; this.replanAt = this.time + 5 + Math.random() * 2;
      if (!this.path) { // no route: point straight at it and hope
        const dx = this.goal.x - car.x, dz = this.goal.z - car.z; let err = Math.atan2(dx, dz) - car.yaw;
        while (err > Math.PI) err -= 2 * Math.PI; while (err < -Math.PI) err += 2 * Math.PI;
        inp.steer = this.lastSteer = Math.max(-1, Math.min(1, err * 1.5)); inp.brake = 0; return inp;
      }
    }
    const p = pursue(car, this.path!, this.idx);
    this.idx = p.idx;
    const sloppy = (1 - this.opts.skill) * 0.2 * Math.sin(this.time * 1.7 + this.wanderSeed);
    inp.steer = this.lastSteer = Math.max(-1, Math.min(1, p.steer + sloppy));
    const targetSpeed = p.speed * (0.6 + 0.4 * this.opts.skill);
    const maxV = DEFAULT_TUNING.maxSpeed * (this.opts.topSpeed ?? 0.72) * targetSpeed;
    // ease off before braking: coast when a little over, brake when well over
    inp.throttle = car.speed > maxV ? 0 : 1;
    inp.brake = car.speed > maxV + 2.5 ? 1 : 0;
    const d = this.distanceToGoal(car);
    const stopDist = 2.2 + (car.speed * car.speed) / (2 * 18);
    if (d < stopDist) { inp.brake = car.speed > 1 || d < 2.2 ? 1 : 0; inp.throttle = 0; }
    return inp;
  }
}
